import type { IconSpec } from '../types';
import { Logger } from './Logger';

const logger = Logger.scope('IconRenderer');

/**
 * IconRenderer - Handles rendering of different icon types
 */
export class IconRenderer {
  private readonly icons: Record<string, string> = {
    info: '<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M12 18.5a6.5 6.5 0 100-13 6.5 6.5 0 000 13z"/></svg>',
    chart: '<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3v18M3 11h18M19 13v6M13 7v12M7 15v4"/></svg>',
    doc: '<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h10M7 11h10M7 15h10M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H9L5 9v8a2 2 0 002 2z"/></svg>',
    theme: '<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3a9 9 0 000 18 9 9 0 110-18z"/></svg>',
  };

  // Glyphicons to FontAwesome mapping
  private readonly glyphiconToFA: Record<string, string> = {
    'gi-cargo': 'fa-solid fa-truck',
    'gi-bell': 'fa-solid fa-bell',
    'gi-home': 'fa-solid fa-house',
    'gi-user': 'fa-solid fa-user',
    'gi-search': 'fa-solid fa-magnifying-glass',
    'gi-envelope': 'fa-solid fa-envelope',
    'gi-heart': 'fa-solid fa-heart',
    'gi-star': 'fa-solid fa-star',
    'gi-trash': 'fa-solid fa-trash',
    'gi-pencil': 'fa-solid fa-pencil',
    'gi-file': 'fa-solid fa-file',
    'gi-folder': 'fa-solid fa-folder',
    'gi-calendar': 'fa-solid fa-calendar',
    'gi-cog': 'fa-solid fa-gear',
    'gi-download': 'fa-solid fa-download',
    'gi-upload': 'fa-solid fa-upload',
    'gi-print': 'fa-solid fa-print',
    'gi-lock': 'fa-solid fa-lock',
    'gi-unlock': 'fa-solid fa-unlock',
    'gi-check': 'fa-solid fa-check',
    'gi-remove': 'fa-solid fa-xmark',
    'gi-plus': 'fa-solid fa-plus',
    'gi-minus': 'fa-solid fa-minus',
    'gi-ok': 'fa-solid fa-check',
    'gi-warning-sign': 'fa-solid fa-triangle-exclamation',
    'gi-info-sign': 'fa-solid fa-circle-info',
    'gi-question-sign': 'fa-solid fa-circle-question',
    'gi-exclamation-sign': 'fa-solid fa-circle-exclamation',
  };

  /**
   * Render an icon based on the spec
   */
  public render(spec?: IconSpec): string {
    if (!spec) return this.icons.doc;

    const type = spec.type || "svg";
    let name = spec.name || "doc";

    // Convert Glyphicons to FontAwesome
    if (type === "gi") {
      const converted = this.convertGlyphiconToFA(name);
      if (converted) {
        logger.debug(`Converted Glyphicon "${name}" to FontAwesome "${converted}"`);
        name = converted;
      } else {
        logger.warn(`No FontAwesome mapping found for Glyphicon "${name}", using default icon`);
        name = "fa-solid fa-circle-question"; // fallback icon
      }
    }

    // Render FontAwesome or converted Glyphicon
    if (type === "fa" || type === "gi") {
      return `<i class="${name} sidebar-nav-icon"></i>`;
    }

    switch (type) {
      case "svg":
        return this.icons[name] || this.icons.doc;
      case "glyph":
        return `<span class="${name}"></span>`;
      default:
        return this.icons.doc;
    }
  }

  /**
   * Convert Glyphicons class name to FontAwesome equivalent
   */
  private convertGlyphiconToFA(glyphiconName: string): string | null {
    // Extract the icon name from "gi gi-iconname" format
    const match = glyphiconName.match(/gi\s+gi-([a-z-]+)/);
    if (match) {
      const iconKey = `gi-${match[1]}`;
      return this.glyphiconToFA[iconKey] || null;
    }
    return null;
  }
}
